import type { HttpLayer } from '../http.js';
import type { HistoryCache, HistoryLane } from '../cache/history-cache.js';
import type { SessionsResource } from '../resources/sessions.js';
import type { Message } from '../types/sessions.js';
import type { TranscriptDemand, TranscriptGap } from './types.js';
import { capMessages, firstGap, retainMessages } from './history-window.js';
import { emitLifecycleEvent } from '../lifecycle/observability.js';

export interface ParticipantTranscriptDeps {
  sessions: SessionsResource;
  http: HttpLayer;
  historyCache: HistoryCache;
  workspaceId: string | undefined;
  sessionId: string;
  participantId: string;
  pageSize: number;
  ceiling: number;
  onError: (message: string) => void;
}

/**
 * One subagent participant's conversation, read through the same keyset
 * windows as the chat transcript (sdk ADR-89da5), cached per
 * `(session, participant)` (sdk ADR-4f834) and bounded the same way
 * (sdk ADR-f43c7): a resident tail page plus at most one history segment,
 * trimmed by the view's demand and kept under the ceiling. A released span
 * between the history segment and the tail is reported as {@link gap}.
 */
export class ParticipantTranscript {
  private rows: Message[] = [];
  private byId = new Set<string>();
  private initialized = false;
  private exhausted = false;
  private disposed = false;
  private tailLoad: Promise<void> | null = null;
  private olderLoad: Promise<readonly Message[]> | null = null;
  private newerLoad: Promise<readonly Message[]> | null = null;
  private catchUp: Promise<void> | null = null;
  private catchUpAgain = false;
  private catchUpBeforeInit = false;
  private handlers = new Set<() => void>();
  private readonly lane: HistoryLane;

  constructor(private readonly deps: ParticipantTranscriptDeps) {
    this.lane = { sessionId: deps.sessionId, participantId: deps.participantId };
  }

  get participantId(): string {
    return this.deps.participantId;
  }

  get messages(): readonly Message[] {
    return this.rows;
  }

  get loaded(): boolean {
    return this.initialized;
  }

  get hasOlder(): boolean {
    return this.initialized && !this.exhausted && this.rows.length > 0;
  }

  /** Rows released between the loaded history segment and the tail; refilled by {@link loadNewer}. */
  get gap(): TranscriptGap | null {
    const gap = firstGap(this.rows, (m) => m.message_index, Infinity);
    return gap ? { afterMessageIndex: gap.lo, beforeMessageIndex: gap.hi } : null;
  }

  subscribe(handler: () => void): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /** The tail page: a cached page set paints at once and revalidates behind it. Idempotent. */
  loadTail(): Promise<void> {
    if (this.initialized) return Promise.resolve();
    if (this.tailLoad) return this.tailLoad;
    this.tailLoad = this.loadTailUncached().finally(() => {
      this.tailLoad = null;
    });
    return this.tailLoad;
  }

  /** One page below the oldest loaded row. Concurrent calls share one read. */
  loadOlder(): Promise<readonly Message[]> {
    if (!this.hasOlder) return Promise.resolve([]);
    if (this.olderLoad) return this.olderLoad;
    this.olderLoad = this.loadOlderUncached().finally(() => {
      this.olderLoad = null;
    });
    return this.olderLoad;
  }

  /** One page above the detached history segment, refilling {@link gap}. Empty when nothing is released. */
  loadNewer(): Promise<readonly Message[]> {
    if (this.gap === null || !this.initialized) return Promise.resolve([]);
    if (this.newerLoad) return this.newerLoad;
    this.newerLoad = this.loadNewerUncached().finally(() => {
      this.newerLoad = null;
    });
    return this.newerLoad;
  }

  /**
   * The rows the view shows, or `null` when it shows none. Keeps the hull of
   * the demand plus one page of margin and the tail, under the ceiling, and
   * releases the rest from memory and the saved history (sdk ADR-f43c7).
   */
  setViewportDemand(range: TranscriptDemand | null): void {
    if (!this.initialized) return;
    this.replaceRows(
      retainMessages(
        this.rows,
        this.tailFloor(),
        range ? { lo: range.oldestMessageIndex, hi: range.newestMessageIndex } : null,
        this.deps.pageSize,
        this.deps.ceiling,
      ),
    );
  }

  /** The participant's lane grew: read forward from the newest loaded row until caught up. */
  scheduleCatchUp(): void {
    if (this.disposed) return;
    if (!this.initialized) {
      this.catchUpBeforeInit = true;
      return;
    }
    if (this.catchUp) {
      this.catchUpAgain = true;
      return;
    }
    this.catchUp = this.catchUpForward()
      .catch((err: unknown) => this.deps.onError(err instanceof Error ? err.message : String(err)))
      .finally(() => {
        this.catchUp = null;
        if (this.catchUpAgain) {
          this.catchUpAgain = false;
          this.scheduleCatchUp();
        }
      });
  }

  /** Frees the window: drops its rows and stops notifying. Reads in flight land nowhere. */
  dispose(): void {
    this.disposed = true;
    this.handlers.clear();
    this.rows = [];
    this.byId.clear();
  }

  private async loadTailUncached(): Promise<void> {
    const identity = await this.deps.http.actingAccountId();
    const cached = this.deps.historyCache.lookup(identity, this.deps.workspaceId, this.lane);
    if (!cached) {
      await this.fetchTail(identity);
      return;
    }
    if (this.disposed) return;
    this.merge(cached.tail.messages);
    for (const p of cached.olderPages) this.merge(p.messages);
    if (cached.exhausted) this.exhausted = true;
    this.initialized = true;
    this.notify();
    this.runDeferredCatchUp();
    void this.revalidate(identity);
  }

  private async fetchTail(identity: string): Promise<void> {
    const page = await this.deps.sessions.getMessagesPage(this.deps.workspaceId, this.deps.sessionId, {
      latest: true,
      limit: this.deps.pageSize,
      participantId: this.deps.participantId,
    });
    if (this.disposed) return;
    const exhausted = page.length < this.deps.pageSize;
    const newestCached = this.rows[this.rows.length - 1];
    const disjoint =
      newestCached !== undefined && page.length > 0 && page[0].message_index > newestCached.message_index + 1;
    if (disjoint) {
      this.rows = [];
      this.byId.clear();
      this.exhausted = false;
      this.deps.historyCache.store(identity, this.deps.workspaceId, this.lane, {
        tail: { messages: page, fetchedAt: Date.now() },
        olderPages: [],
        exhausted,
      });
    } else {
      this.deps.historyCache.replaceTail(
        identity,
        this.deps.workspaceId,
        this.lane,
        { messages: page, fetchedAt: Date.now() },
        exhausted,
      );
    }
    this.merge(page);
    if (exhausted) this.exhausted = true;
    this.initialized = true;
    this.notify();
    this.runDeferredCatchUp();
  }

  private async revalidate(identity: string): Promise<void> {
    try {
      await this.fetchTail(identity);
    } catch (err) {
      // no-fallbacks.md exception: the cached snapshot stays visible and the failure is logged; reconnect heals (sdk ADR-b2a2e).
      emitLifecycleEvent('cache.history_revalidate_failed', {
        session_id: this.deps.sessionId,
        cause: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private async loadOlderUncached(): Promise<readonly Message[]> {
    const cursor = this.rows[0].message_index;
    const page = await this.deps.sessions.getMessagesPage(this.deps.workspaceId, this.deps.sessionId, {
      beforeMessageIndex: cursor,
      limit: this.deps.pageSize,
      participantId: this.deps.participantId,
    });
    if (this.disposed) return page;
    const detached = this.gap !== null;
    this.merge(page);
    if (page.length < this.deps.pageSize) this.exhausted = true;
    if (!detached) {
      const identity = await this.deps.http.actingAccountId();
      this.deps.historyCache.appendOlderPage(identity, this.deps.workspaceId, this.lane, cursor, page);
    }
    this.replaceRows(capMessages(this.rows, this.tailFloor(), this.deps.ceiling, 'newest'));
    this.notify();
    return page;
  }

  private async loadNewerUncached(): Promise<readonly Message[]> {
    const gap = this.gap;
    if (gap === null) return [];
    const page = await this.deps.sessions.getMessagesPage(this.deps.workspaceId, this.deps.sessionId, {
      afterMessageIndex: gap.afterMessageIndex,
      limit: this.deps.pageSize,
      participantId: this.deps.participantId,
    });
    if (this.disposed) return page;
    this.merge(page);
    this.replaceRows(capMessages(this.rows, this.tailFloor(), this.deps.ceiling, 'oldest'));
    this.notify();
    return page;
  }

  private runDeferredCatchUp(): void {
    if (!this.catchUpBeforeInit) return;
    this.catchUpBeforeInit = false;
    this.scheduleCatchUp();
  }

  private async catchUpForward(): Promise<void> {
    const identity = await this.deps.http.actingAccountId();
    for (;;) {
      const newest = this.rows[this.rows.length - 1];
      if (newest === undefined) {
        await this.fetchTail(identity);
        return;
      }
      const page = await this.deps.sessions.getMessagesPage(this.deps.workspaceId, this.deps.sessionId, {
        afterMessageIndex: newest.message_index,
        limit: this.deps.pageSize,
        participantId: this.deps.participantId,
      });
      if (this.disposed || page.length === 0) return;
      this.merge(page);
      this.deps.historyCache.extendTail(identity, this.deps.workspaceId, this.lane, page);
      this.replaceRows(capMessages(this.rows, this.tailFloor(), this.deps.ceiling, 'oldest'));
      this.notify();
      if (page.length < this.deps.pageSize) return;
    }
  }

  private tailFloor(): number {
    const n = this.rows.length;
    if (n === 0) return 0;
    return this.rows[Math.max(0, n - this.deps.pageSize)].message_index;
  }

  private replaceRows(next: Message[]): void {
    if (next.length === this.rows.length) return;
    const prevOldest = this.rows[0]?.message_index;
    const kept = new Set(next.map((m) => m.id));
    for (const m of this.rows) {
      if (!kept.has(m.id)) this.byId.delete(m.id);
    }
    this.rows = next;
    if (prevOldest !== undefined && (this.rows[0]?.message_index ?? Infinity) > prevOldest) this.exhausted = false;
    this.persistWindow();
    this.notify();
  }

  // (sdk ADR-f43c7)
  private persistWindow(): void {
    const n = this.rows.length;
    if (n === 0) return;
    let floor = n - 1;
    while (floor > 0 && this.rows[floor - 1].message_index === this.rows[floor].message_index - 1) floor--;
    const contiguousFrom = this.rows[floor].message_index;
    void this.deps.http
      .actingAccountId()
      .then((identity) => this.deps.historyCache.dropBelow(identity, this.deps.workspaceId, this.lane, contiguousFrom))
      .catch((err: unknown) => this.deps.onError(err instanceof Error ? err.message : String(err)));
  }

  private merge(page: readonly Message[]): void {
    const added = page.filter((m) => !this.byId.has(m.id));
    if (added.length === 0) return;
    for (const m of added) this.byId.add(m.id);
    this.rows = [...this.rows, ...added].sort((a, b) => a.message_index - b.message_index);
  }

  private notify(): void {
    if (this.disposed) return;
    for (const handler of this.handlers) handler();
  }
}
